import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { permissionTagsAPI, reportsAPI } from '../../services/api';
import { usePermission } from '../../contexts/PermissionContext';
import toast from 'react-hot-toast';

/**
 * 報表權限標籤設定 Modal
 * 用於設定報表的權限標籤（必須至少設定一個才能操作報表）
 * @param {Object} report - 報表物件
 * @param {boolean} isEditMode - 是否為編輯模式（false表示初始設定，true表示編輯現有標籤）
 * @param {Function} onClose - 關閉 Modal
 * @param {Function} onSuccess - 成功後的回調
 */
export default function ReportTagSetupModal({ report, isEditMode = false, onClose, onSuccess }) {
  const { hasWildcardAccess, getVisibleTags } = usePermission();
  const [selectedTags, setSelectedTags] = useState(report.access_tags || []);
  const [saving, setSaving] = useState(false);

  // 取得權限標籤列表
  const { data: tagsData, isLoading: tagsLoading } = useQuery({
    queryKey: ['permissionTags'],
    queryFn: permissionTagsAPI.getAll,
  });

  const allPermissionTags = tagsData?.data?.filter(tag => tag.is_active !== false) || [];

  // 根據使用者的 visible_tags 過濾可選的標籤
  const visibleTags = getVisibleTags();
  const permissionTags = useMemo(() => {
    if (hasWildcardAccess()) {
      // 管理員可以看到所有標籤
      return allPermissionTags;
    }
    // 非管理員只能看到自己 visible_tags 內的標籤，以及報表現有的標籤
    return allPermissionTags.filter(tag =>
      visibleTags.includes(tag.name) ||
      (report.access_tags || []).includes(tag.name)
    );
  }, [allPermissionTags, visibleTags, hasWildcardAccess, report.access_tags]);

  // 切換標籤選擇
  const toggleTag = (tagName) => {
    setSelectedTags(prev =>
      prev.includes(tagName)
        ? prev.filter(t => t !== tagName)
        : [...prev, tagName]
    );
  };

  // 儲存
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedTags.length === 0) {
      toast.error('請至少選擇一個權限標籤');
      return;
    }

    setSaving(true);
    try {
      await reportsAPI.update(report.id, { access_tags: selectedTags });
      toast.success('權限標籤設定成功');
      onSuccess();
    } catch (error) {
      toast.error(error.message || '設定失敗');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={`flex justify-between items-center px-6 py-4 border-b flex-shrink-0 ${isEditMode ? 'bg-blue-50' : 'bg-yellow-50'}`}>
          <div>
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              {isEditMode ? (
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              )}
              {isEditMode ? '編輯權限標籤' : '設定權限標籤'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              報表：{report.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* 警告提示 - 只在初始設定時顯示 */}
          {!isEditMode && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              <p className="font-medium">此報表尚未設定權限標籤</p>
              <p className="mt-1">必須設定至少一個權限標籤後，才能檢視報表資料或在專案中使用。</p>
            </div>
          )}

          {/* 標籤選擇 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              選擇權限標籤 <span className="text-red-500">*</span>
            </label>
            {tagsLoading ? (
              <div className="text-center py-4 text-gray-500">載入中...</div>
            ) : permissionTags.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                尚未建立任何權限標籤，請先至設定頁面建立。
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {permissionTags.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.name)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                      selectedTags.includes(tag.name)
                        ? 'text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    style={selectedTags.includes(tag.name) ? { backgroundColor: tag.color } : {}}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            )}
            {selectedTags.length > 0 && (
              <p className="text-xs text-blue-600 mt-2">
                已選擇 {selectedTags.length} 個標籤
              </p>
            )}
          </div>

          {/* 說明 */}
          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
            <p className="font-medium mb-1">設定後的效果：</p>
            <ul className="list-disc list-inside space-y-1 text-gray-500">
              <li>只有擁有所選標籤的用戶可以看到此報表</li>
              <li>專案必須有對應標籤才能 Import 此報表</li>
            </ul>
          </div>

          {/* 按鈕 */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              disabled={saving}
            >
              取消
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={saving || selectedTags.length === 0}
            >
              {saving ? '儲存中...' : '儲存設定'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
